// src/services/ghlWebhook.js
// Sends the completed shed-configurator quote to a GoHighLevel Inbound Webhook
// (Automation → Workflows → "Inbound Webhook" trigger). Fire-and-forget: a GHL
// outage must never block the customer from reaching Stripe checkout.
//
// The payload is intentionally FLAT — one key per mappable value — because
// GHL's webhook field-mapper works best with flat keys. Keep key names stable:
// renaming a key breaks the mapping saved inside the GHL workflow.
// Field reference doc: docs/GHL_WEBHOOK_INTEGRATION.md

const WEBHOOK_URL = import.meta.env.VITE_GHL_WEBHOOK_URL;

/** Map each configurator stepId to the flat payload key GHL will see. */
const STEP_KEYS = {
  paint: 'paint_package',
  roofing: 'roofing',
  flooring: 'flooring',
  foundation: 'site_leveling',
  workbench: 'workbench',
  loft: 'loft',
  shelving: 'shelving',
  venting: 'venting',
  window: 'window',
  delivery: 'delivery_access',
  anchors: 'anchors',
  ramp: 'ramp',
};

/** Flatten the configurator QuotePayload into GHL-mappable keys. */
export function buildGhlPayload(quote) {
  const { model, dimensions, selections, totals, lead, meta } = quote;

  const totalDollars = Math.round(totals.totalCents) / 100;

  const payload = {
    // --- Contact (map to GHL standard fields) ---
    first_name: lead.firstName,
    last_name: lead.lastName,
    full_name: `${lead.firstName} ${lead.lastName}`.trim(),
    email: lead.email,
    phone: lead.phone,
    postal_code: lead.zip,

    // --- Build (map to GHL custom fields) ---
    shed_model: model.name,
    shed_size: `${dimensions.w}x${dimensions.l}`,
    shed_width_ft: dimensions.w,
    shed_length_ft: dimensions.l,
    floor_sq_ft: dimensions.floorSqFt,

    // --- Quote money (numbers, for opportunity value) ---
    quote_total: totalDollars,
    quote_total_formatted: totals.total,
    deposit_due: Math.round(totalDollars * 0.25),

    // --- Attribution ---
    lead_source: 'lowesheds.com shed configurator',
    page_url: typeof window !== 'undefined' ? window.location.href : '',
    submitted_at: meta.createdAt,
    configurator_version: meta.configuratorVersion,
  };

  // One key per step, e.g. flooring: "Upgraded Flooring — Color: Alabaster ($480.00)"
  for (const li of selections) {
    const key = STEP_KEYS[li.stepId];
    if (!key) continue;
    payload[key] = li.included ? `${li.detail} (included)` : `${li.detail} (${li.price})`;
  }

  // Yes/no flags for the two follow-up-driving answers, for clean GHL branching.
  const byStep = Object.fromEntries(selections.map((li) => [li.stepId, li]));
  payload.needs_site_leveling = byStep.foundation?.optionId === 'unlevel' ? 'yes' : 'no';
  payload.delivery_obstruction = byStep.delivery?.optionId === 'obstruction' ? 'yes' : 'no';

  // Full human-readable configuration, one line per step (for notes/email merge).
  payload.configuration_summary = selections
    .map((li) => `${li.label}: ${li.detail} — ${li.included ? 'Included' : li.price}`)
    .join('\n');

  return payload;
}

/**
 * POST the quote to the GHL inbound webhook. Never throws; resolves once the
 * request has been handed to the browser. No-op when VITE_GHL_WEBHOOK_URL is
 * unset so dev environments don't spam the production workflow.
 */
export function sendQuoteToGhl(quote) {
  if (!WEBHOOK_URL) return Promise.resolve();
  return fetch(WEBHOOK_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(buildGhlPayload(quote)),
    // Survives the page handing off to Stripe checkout mid-flight.
    keepalive: true,
  }).catch((err) => {
    console.error('GHL webhook send failed (checkout continues):', err);
  });
}
